import React, { useState, useCallback, useMemo, useRef } from 'react';

type FileInput = {
    file: File & { originalFileSize?: number; fileType?: string };
    localUrl: string;
};

type FilesInput = {
    files: File & { originalFileSize?: number; fileType?: string }[];
};

type LinkOnly = string;

export const ReactCropDefaults: { [key: string]: React.CSSProperties } = {
    style: {
        objectFit: 'contain',
        height: '100%',
        width: '100%',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    imageStyle: {
        objectFit: 'contain',
        height: '100%',
        width: '100%',
        position: 'relative',
    },
};

const BASE_BYTES_CONVERSION = 1024 * 1024;
const MAX_BYTES = 10 * BASE_BYTES_CONVERSION;

const defaultPredicate = () => true;

function filterFileTypes(
    allFiles: FileList,
    predicate: (file: File) => boolean = defaultPredicate,
) {
    const fileList: File[] = [];

    let length = Math.min(allFiles?.length ?? 0, 5);
    for (let i = 0; i < length; i++) {
        const f = allFiles[i];

        if (predicate(f)) {
            fileList.push(f);
        }
    }

    return fileList;
}

export function useImageInput({
    defaultWidth = 200,
    defaultHeight = 200,
    updateNow,
    immediateUpdate,
    multiple,
    fileTypeFilter = () => true,
}: {
    defaultWidth?: number;
    defaultHeight?: number;
    updateNow?: (file: File) => void;
    immediateUpdate?: boolean;
    multiple?: boolean;
    fileTypeFilter?: (file: File) => boolean;
} = {}) {
    const [crop, setCrop] = useState<any>({
        x: 0,
        y: 0,
        width: defaultWidth,
        height: defaultHeight,
    });
    const [fileInput, setFileInput] = useState<FileInput | null>(null);
    const [filesInput, setManyFiles] = useState<FilesInput | null>(null);
    const [croppedInput, setCroppedInput] = useState<FileInput | null>(null);
    const [linkUrl, setLinkUrl] = useState<LinkOnly | null>(null);

    const createFileInput = useCallback(
        (file: File) => {
            if (!fileTypeFilter(file)) return;
            setFileInput({
                file,
                localUrl: URL.createObjectURL(file),
            });
            if (immediateUpdate && updateNow) {
                updateNow?.(file);
            }
        },
        [setFileInput, updateNow, immediateUpdate, fileTypeFilter],
    );

    const createManyFileInput = useCallback(
        (files: FileList) => {
            const filteredFiles = filterFileTypes(files, fileTypeFilter);
            setManyFiles({
                files: filteredFiles as any,
            });
            if (immediateUpdate && updateNow) {
                if (filteredFiles?.length) {
                    let length = Math.min(filteredFiles?.length ?? 0, 5);
                    for (let i = 0; i < length; i++) {
                        const f = filteredFiles[i];
                        updateNow?.(f);
                    }
                }
            }
        },
        [setManyFiles, updateNow, fileTypeFilter],
    );

    const createFilesAll = useCallback(
        (files: FileList) => {
            if (multiple) {
                createManyFileInput(files);
            } else {
                createFileInput(files?.[0]);
            }
        },
        [createManyFileInput, createFileInput],
    );

    const clearFileInput = useCallback(() => {
        setFileInput(null);
    }, [setFileInput]);

    const toCropAttributes = useCallback((cropItem: typeof crop) => {
        return {
            fileCropX: Math.floor(cropItem.x),
            fileCropY: Math.floor(cropItem.y),
            fileCropW: Math.floor(cropItem.width),
            fileCropH: Math.floor(cropItem.height),
        };
    }, []);

    const getLink = useCallback(
        (file: any) => {
            setFileInput({
                file,
                localUrl: file?.link,
            });
        },
        [setFileInput],
    );

    const fileInputEventHandler = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            e.preventDefault();

            const file = e.target.files;
            if (file?.length && !multiple) {
                createFileInput(file?.[0]);
            } else if (file && file?.length && multiple) {
                createManyFileInput(file);
            }
        },
        [createFileInput],
    );

    const fileDropEventHandler = useCallback(
        (e: React.DragEvent<HTMLInputElement>) => {
            e.preventDefault();

            let file = e.dataTransfer.files;
            if (file?.length && !multiple) {
                createFileInput(file?.[0]);
            } else if (file && file?.length && multiple) {
                createManyFileInput(file);
            }
        },
        [createFileInput],
    );

    const handlers = useMemo(
        () => ({
            fileInputEventHandler,
            fileDropEventHandler,
            createFileInput,
            createFilesAll,
            clearFileInput,
            setCrop,
            setCroppedInput,
            getLink,
            toCropAttributes,
            setFileInput,
        }),
        [setFileInput, setCrop],
    );

    return {
        crop,
        fileInput,
        croppedInput,
        linkUrl,
        validInput: linkUrl || fileInput,
        handlers,
    };
}
